import type { User } from '@supabase/supabase-js';
import type { SubscriptionStatus } from '@/types';
import type { TeamMember, TeamJoinRequest } from '@/types/team';

export const DEMO_PRIVACY_EMAILS = ['gags0384@gmail.com'] as const;

export const DEMO_PRIVACY_PLACEHOLDERS = {
  name: 'Demo Advocate',
  email: 'demo@indiacasestatus.com',
  phone: '+91 90000 00000',
  teamName: 'Test Firm',
} as const;

type AuthLikeUser = Pick<User, 'email' | 'phone' | 'user_metadata'>;

type ProfileLike = {
  name?: string | null;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
};

type TeamLike = {
  name?: string | null;
  brand_name?: string | null;
};

export type DemoPrivacyReplacement = {
  from: string;
  to: string;
};

function normalizedEmail(value?: string | null): string {
  return value?.trim().toLowerCase() || '';
}

export function isDemoPrivacyEmail(value?: string | null): boolean {
  const email = normalizedEmail(value);
  return DEMO_PRIVACY_EMAILS.some((allowed) => normalizedEmail(allowed) === email);
}

export function isDemoPrivacyUser(user?: Partial<AuthLikeUser> | null): boolean {
  if (!user) return false;
  if (isDemoPrivacyEmail(user.email)) return true;

  const metadata = user.user_metadata as Record<string, unknown> | null | undefined;
  return isDemoPrivacyEmail(typeof metadata?.email === 'string' ? metadata.email : null);
}

export function displayAuthEmail(email?: string | null): string | null {
  return isDemoPrivacyEmail(email) ? DEMO_PRIVACY_PLACEHOLDERS.email : email || null;
}

export function displayAuthPhone(user?: Partial<AuthLikeUser> | null): string | null {
  return isDemoPrivacyUser(user) ? DEMO_PRIVACY_PLACEHOLDERS.phone : user?.phone || null;
}

export function displayAuthName(user?: Partial<AuthLikeUser> | null): string | null {
  if (!isDemoPrivacyUser(user)) {
    const metadata = user?.user_metadata as Record<string, unknown> | null | undefined;
    const name = metadata?.full_name || metadata?.name;
    return typeof name === 'string' && name.trim() ? name.trim() : null;
  }
  return DEMO_PRIVACY_PLACEHOLDERS.name;
}

export function maskDemoProfileForUser<T extends ProfileLike>(profile: T, user?: Partial<AuthLikeUser> | null): T {
  const profileEmail = profile.email || null;
  if (!isDemoPrivacyUser(user) && !isDemoPrivacyEmail(profileEmail)) return profile;

  return {
    ...profile,
    name: DEMO_PRIVACY_PLACEHOLDERS.name,
    full_name: DEMO_PRIVACY_PLACEHOLDERS.name,
    email: DEMO_PRIVACY_PLACEHOLDERS.email,
    phone: DEMO_PRIVACY_PLACEHOLDERS.phone,
    mobile: DEMO_PRIVACY_PLACEHOLDERS.phone,
  };
}

export function maskDemoTeamForUser<T extends TeamLike>(team: T, user?: Partial<AuthLikeUser> | null): T {
  if (!isDemoPrivacyUser(user)) return team;

  return {
    ...team,
    name: DEMO_PRIVACY_PLACEHOLDERS.teamName,
    brand_name: team.brand_name ? DEMO_PRIVACY_PLACEHOLDERS.teamName : team.brand_name,
  };
}

export function maskDemoSubscriptionStatusForUser<T extends SubscriptionStatus>(
  status: T,
  user?: Partial<AuthLikeUser> | null,
): T {
  if (!isDemoPrivacyUser(user) || !status.team) return status;
  return {
    ...status,
    team: {
      ...status.team,
      name: DEMO_PRIVACY_PLACEHOLDERS.teamName,
    },
  };
}

export function maskDemoTeamMembersForUser<T extends TeamMember | TeamJoinRequest>(
  members: T[],
  user?: Partial<AuthLikeUser> | null,
): T[] {
  if (!isDemoPrivacyUser(user)) return members;
  return members.map((member) => ({
    ...member,
    profile: member.profile
      ? maskDemoProfileForUser(member.profile, user)
      : member.profile,
  }));
}

function uniqueNonEmpty(values: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const raw of values) {
    const value = raw?.trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }

  return out;
}

function nameVariants(value?: string | null): string[] {
  const raw = value?.trim();
  if (!raw) return [];

  const parts = raw.split(/\s+/).filter(Boolean);
  return uniqueNonEmpty([raw, parts[0], parts.length > 1 ? parts[parts.length - 1] : null]);
}

function phoneVariants(value?: string | null): string[] {
  const raw = value?.trim();
  if (!raw) return [];

  const digits = raw.replace(/\D/g, '');
  const local10 = digits.length >= 10 ? digits.slice(-10) : '';
  const withCountry = local10 ? `91${local10}` : '';
  const e164 = local10 ? `+91${local10}` : '';
  const spaced = local10 ? `+91 ${local10.slice(0, 5)} ${local10.slice(5)}` : '';

  return uniqueNonEmpty([raw, digits, local10, withCountry, e164, spaced]);
}

export function buildDemoPrivacyReplacements(input: {
  user?: Partial<AuthLikeUser> | null;
  profile?: ProfileLike | null;
  team?: TeamLike | null;
}): DemoPrivacyReplacement[] {
  if (!isDemoPrivacyUser(input.user) && !isDemoPrivacyEmail(input.profile?.email)) return [];

  const metadata = input.user?.user_metadata as Record<string, unknown> | null | undefined;
  const metadataName = typeof metadata?.full_name === 'string'
    ? metadata.full_name
    : typeof metadata?.name === 'string'
      ? metadata.name
      : null;
  const metadataEmail = typeof metadata?.email === 'string' ? metadata.email : null;

  const emails = uniqueNonEmpty([
    input.user?.email,
    metadataEmail,
    input.profile?.email,
    ...DEMO_PRIVACY_EMAILS,
  ]);
  const names = uniqueNonEmpty([
    ...nameVariants(input.profile?.name),
    ...nameVariants(input.profile?.full_name),
    ...nameVariants(metadataName),
  ]);
  const teams = uniqueNonEmpty([
    input.team?.name,
    input.team?.brand_name,
  ]);
  const phones = uniqueNonEmpty([
    ...phoneVariants(input.user?.phone),
    ...phoneVariants(input.profile?.phone),
    ...phoneVariants(input.profile?.mobile),
  ]);

  return [
    ...emails.map((from) => ({ from, to: DEMO_PRIVACY_PLACEHOLDERS.email })),
    ...names.map((from) => ({ from, to: DEMO_PRIVACY_PLACEHOLDERS.name })),
    ...teams.map((from) => ({ from, to: DEMO_PRIVACY_PLACEHOLDERS.teamName })),
    ...phones.map((from) => ({ from, to: DEMO_PRIVACY_PLACEHOLDERS.phone })),
  ].sort((a, b) => b.from.length - a.from.length);
}
